import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';

const AnimatedButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  fontWeight: 'bold',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  zIndex: 1,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[4],
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
  height: '48px',
  width: '144px',
  fontSize: '1rem',
}));

const SnsCampaignBanner = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getLayout = () => {
    if (isMobile) {
      return {
        viewBox: '0 0 360 280',
        width: '100%',
        height: '200px',
        mainTitle: { x: 30, y: 70, size: '24px' },
        subTitle1: { x: 30, y: 110, size: '20px' },
        subTitle2: { x: 30, y: 145, size: '20px' },
        point: { x: 30, y: 200, size: '36px' }
      };
    }
    return {
      viewBox: '0 0 800 200',
      width: '100%',
      height: '200px',
      mainTitle: { x: 50, y: 70, size: '28px' },
      subTitle1: { x: 50, y: 110, size: '22px' },
      subTitle2: { x: 50, y: 145, size: '22px' },
      point: { x: 500, y: 100, size: '42px', circle: 60 },
      button: { x: 350, y: 160 }
    };
  };

  const layout = getLayout();
  const colors = {
    primary: '#1976d2',
    secondary: '#42a5f5',
    accent: '#1565c0',
    background: '#F0F9FF',
    text: '#1565c0'
  };

  if (!mounted) return null;

  return (
    <Box className="w-full max-w-6xl mx-auto px-4" style={{cursor: "pointer"}} onClick={() => window.location.href = "https://forms.gle/sYm7MkuYd83T7ruk7"}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={layout.viewBox}
        style={{ width: layout.width, height: layout.height }}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="bgGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor={colors.background} />
          </linearGradient>
        </defs>

        <rect width="100%" height="100%" fill="url(#bgGradient)" />

        <g opacity="0.3">
          <path 
            d={isMobile ? 
              "M-50 50 C 100 20, 200 80, 400 50" : 
              "M-100 50 C 200 0, 400 100, 900 50"
            }
            stroke={colors.primary} 
            fill="none"
            strokeWidth="1"
          >
            <animate
              attributeName="d"
              dur="15s"
              repeatCount="indefinite"
              values={isMobile ?
                "M-50 50 C 100 20, 200 80, 400 50;M-50 50 C 100 80, 200 20, 400 50;M-50 50 C 100 20, 200 80, 400 50" :
                "M-100 50 C 200 0, 400 100, 900 50;M-100 50 C 200 100, 400 0, 900 50;M-100 50 C 200 0, 400 100, 900 50"
              }
              calcMode="spline"
              keySplines="0.4 0 0.6 1; 0.4 0 0.6 1"
            />
          </path>
          <path 
            d={isMobile ? 
              "M-50 70 C 100 40, 200 100, 400 70" : 
              "M-100 70 C 200 20, 400 120, 900 70"
            }
            stroke={colors.primary} 
            fill="none"
            strokeWidth="1"
          >
            <animate
              attributeName="d"
              dur="12s"
              repeatCount="indefinite"
              values={isMobile ?
                "M-50 70 C 100 40, 200 100, 400 70;M-50 70 C 100 100, 200 40, 400 70;M-50 70 C 100 40, 200 100, 400 70" :
                "M-100 70 C 200 20, 400 120, 900 70;M-100 70 C 200 120, 400 20, 900 70;M-100 70 C 200 20, 400 120, 900 70"
              }
              calcMode="spline"
              keySplines="0.4 0 0.6 1; 0.4 0 0.6 1"
            />
          </path>
        </g>

        <text
          x={layout.mainTitle.x}
          y={layout.mainTitle.y}
          fill={colors.text}
          style={{
            fontSize: layout.mainTitle.size,
            fontWeight: 'bold'
          }}
        >
          SNS宣伝キャンペーン
        </text>

        <text
          x={layout.subTitle1.x}
          y={layout.subTitle1.y}
          fill={colors.text}
          style={{ fontSize: layout.subTitle1.size }}
        >
          プロフィールに
        </text>

        <text
          x={layout.subTitle2.x}
          y={layout.subTitle2.y}
          fill={colors.text}
          style={{ fontSize: layout.subTitle2.size }}
        >
          <tspan
            fill={colors.accent}
            style={{ fontWeight: 'bold' }}
          >
            アワートAI
          </tspan>
          <tspan x={layout.subTitle2.x + (isMobile ? 85 : 95)} dy="0">
            のリンクを貼ると
          </tspan>
        </text>

        {isMobile ? (
          <g transform={`translate(${layout.point.x}, ${layout.point.y})`}>
            <text
              fill={colors.text}
              style={{
                fontSize: layout.point.size,
                fontWeight: 'bold'
              }}
            >
              500 POINT
            </text>
            <text
              x="0"
              y="30"
              fill={colors.text}
              style={{
                fontSize: `${parseInt(layout.point.size) * 0.6}px`
              }}
            >
              毎日付与
            </text>
          </g>
        ) : (
          <g transform={`translate(${layout.point.x}, ${layout.point.y})`}>
            <circle
              cx="0"
              cy="0"
              r={layout.point.circle}
              fill="none"
              stroke={colors.accent}
              strokeWidth="2"
              opacity="0.6"
            >
              <animate
                attributeName="r"
                values={`${layout.point.circle-2};${layout.point.circle+2};${layout.point.circle-2}`}
                dur="3s"
                repeatCount="indefinite"
                calcMode="spline"
                keySplines="0.4 0 0.6 1; 0.4 0 0.6 1"
              />
            </circle>
            
            <text
              x="0"
              y="-10"
              fill={colors.text}
              textAnchor="middle"
              style={{
                fontSize: layout.point.size,
                fontWeight: 'bold'
              }}
            >
              500
            </text>
            
            <text
              x="0"
              y="20"
              fill={colors.text}
              textAnchor="middle"
              style={{
                fontSize: `${parseInt(layout.point.size) * 0.4}px`,
                fontWeight: 'bold'
              }}
            >
              POINT
            </text>
            
            <text
              x="0"
              y="45"
              fill={colors.text}
              textAnchor="middle"
              style={{
                fontSize: `${parseInt(layout.point.size) * 0.35}px`
              }}
            >
              毎日付与
            </text>
          </g>
        )}

        {!isMobile && (
          <foreignObject
            x={layout.button.x - 52}
            y={layout.button.y - 24}
            width="144"
            height="48"
          >
            <AnimatedButton variant="contained" disableRipple onClick={() => window.location.href = "https://forms.gle/sYm7MkuYd83T7ruk7"}>
              応募する
            </AnimatedButton>
          </foreignObject>
        )}
      </svg>
    </Box>
  );
};

export default SnsCampaignBanner;